import { Info20Filled } from '@fluentui/react-icons';
import { useGenerateCustomFieldUniqueId, useUpsertCustomFieldValue } from '@modules/items';
import { TCustomFieldRaw } from '@modules/users/types/organizations.types';
import { TDocument, TDraft, TVersion } from '../../../types';
import { TFile, TFileVersion } from '@modules/files';
import isBlockDocument from '../../../helpers/isBlockDocument.helper';
import { getImmediateParentId } from '@modules/items/helpers/items.helper';
import useIsVersionEditable from '../../../hooks/useIsVersionEditable.hook';
import TaggedSnippets from './TaggedSnippets';
import CustomFieldInputs from './CustomFieldInputs';

type TProps = {
  item: TDocument | TFile;
  isDoc: boolean;
  versionId: string;
  version: TVersion | TDraft | TFileVersion;
};

const CustomFields = ({ item, isDoc, versionId, version }: TProps) => {
  const upsertField = useUpsertCustomFieldValue();
  const generateUniqueId = useGenerateCustomFieldUniqueId();
  const isReusableContent = isBlockDocument(item);
  const parentId = getImmediateParentId(item.resolvedPath);
  const itemType = isDoc ? 'DOCUMENT' : 'FILE';

  const isEditable = useIsVersionEditable(versionId, item);

  const saveValue = (fieldId: string, value: TCustomFieldRaw) =>
    upsertField.mutateAsync({ id: item._id, itemType, fieldId, value, parentId });

  return (
    <div className="flex flex-col space-y-4">
      {isDoc && (
        <TaggedSnippets item={item as TDocument} versionId={versionId} version={version as TVersion | TDraft} />
      )}

      <CustomFieldInputs
        customFieldValues={version.customFieldValues}
        fallbackToOrgDefault
        isEditable={isEditable}
        saveValue={saveValue}
        generateUniqueId={
          isReusableContent
            ? undefined
            : async (cf) =>
                (await generateUniqueId.mutateAsync({ id: item._id, itemType, fieldId: cf.id, parentId })).value
        }
      />

      {!isEditable && (
        <div className="pt-2 w-full">
          <p className="flex items-center space-x-1">
            <Info20Filled />
            <span>You can only edit custom fields for a draft or the latest unapproved version.</span>
          </p>
        </div>
      )}
    </div>
  );
};

export default CustomFields;
